import { useEffect, useContext, useState } from "react"
import { Link } from "react-router-dom"
import { Field, useFormikContext } from "formik"
import PropTypes from "prop-types"
import axios from "axios"
import { FormContext } from "../../../context/formContext"
import { FirebaseContext } from "../../../firebase"
import cloudFunctionsUrl from "../../../config/cloudFunctionsEndpoint"
import { Dropdown } from "primereact/dropdown"

import styles from "../../../styles/Form.module.css"

export default function AccountTFA({ field, block, index }) {
  const [polling, setPolling] = useState(false)
  const [pollingCount, setPollingCount] = useState(0)
  const [enrolledFactors, setEnrolledFactors] = useState([])
  const formik = useFormikContext()
  const form = useContext(FormContext)
  const firebase = useContext(FirebaseContext)
  const isDisabled = form ? form.blocksSaved[block] : false

  // Firebase Auth
  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    const unsubscribe = firebase.auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        const userEnrolledFactors = authUser?.multiFactor?.enrolledFactors || []
        setEnrolledFactors(userEnrolledFactors)
        if (userEnrolledFactors.length === 1) {
          formik.setFieldValue(
            field.field_name,
            userEnrolledFactors[0].factorId
          )
        }
        if (userEnrolledFactors.length > 1) {
          const response = await axios.post(
            `${cloudFunctionsUrl()}/checkMfaSettings`,
            { email: firebase.auth.currentUser.email }
          )
          const preferredMethod = response.data.preferred_method
          formik.setFieldValue(field.field_name, preferredMethod)
        }
      }
    })

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Polling for email verification every 5 seconds
  useEffect(() => {
    async function reloadUser() {
      await firebase.auth.currentUser.reload()
      const newAuthUser = firebase.auth.currentUser
      console.log("User reloaded: ", newAuthUser)
      const userEnrolledFactors =
        newAuthUser?.multiFactor?.enrolledFactors || []
      setEnrolledFactors(userEnrolledFactors)
      if (userEnrolledFactors.length > 1) {
        formik.setFieldValue(field.field_name, userEnrolledFactors[0].factorId)
        setPolling(false)
      }
    }
    let interval
    if (polling && pollingCount < 99) {
      interval = setInterval(async () => {
        console.count("Polling on email verification")
        await reloadUser()
        setPollingCount((prev) => prev + 1)
      }, 5000)
    }
    return () => clearInterval(interval)
  }, [polling, pollingCount])

  if (enrolledFactors.length === 0) {
    return (
      <div style={{ order: index }}>
        Your account doesn't have any methods enrolled. You can set up your
        two-factor authentication in the{" "}
        <strong>
          <Link
            to="/account/2fa/settings"
            style={{ textDecoration: "underline" }}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setPolling(true)}
          >
            Two-Factor settings
          </Link>
        </strong>
        .
      </div>
    )
  }

  const handleOptions = () => {
    const handleLabel = (factorId) => {
      switch (factorId) {
        case "phone":
          return "SMS"
        case "totp":
          return "TOTP"
        default:
          return "Unknown"
      }
    }
    return enrolledFactors.map((factor) => {
      return {
        label: handleLabel(factor.factorId),
        value: factor.factorId,
      }
    })
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
      }}
    >
      <h3>✅ Your account has Two-Factor Authentication configured.</h3>
      <label className={styles.label}>
        Preferred Two-Factor Authentication Method:
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              <Dropdown
                placeholder="Please select"
                id={field.field_name}
                name={formikField.name}
                value={formikField.value}
                onChange={formikField.onChange}
                options={handleOptions()}
                disabled={isDisabled}
              />
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

AccountTFA.propTypes = {
  field: PropTypes.shape({
    field_name: PropTypes.string.isRequired,
  }).isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}
